import { deburr } from 'lodash-es';

import {
  GroupItem,
  GroupSection,
  MultiSelectMapping
} from '@design-system/components/m-grouped-dropdown/m-grouped-dropdown.types';

function matchesQuery(text: string, query: string): boolean {
  return deburr(text.toLowerCase()).includes(query);
}

export function filterGroupedSections(
  groups: GroupSection[],
  query: string
): GroupSection[] {
  if (!query) return groups;

  return groups
    .map((group) => {
      if (group.type === 'single') {
        return {
          ...group,
          items: group.items.filter((item) =>
            matchesQuery(item.label ?? '', query)
          )
        };
      }

      const items = group.items
        .map((cat) => ({ ...cat, options: filterMappedCategory(cat, query) }))
        .filter(
          (cat) =>
            matchesQuery(cat.label ?? '', query) ||
            (cat.options?.length ?? 0) > 0
        );
      return { ...group, items };
    })
    .filter((group) => group.items.length > 0);
}

export function filterMappedCategory(
  category: GroupItem,
  query: string
): GroupItem[] {
  if (!query) return category.options ?? [];

  const labelMatch = matchesQuery(category.label ?? '', query);
  return (category.options ?? []).filter(
    (option) =>
      labelMatch ||
      matchesQuery(option.label ?? '', query) ||
      matchesQuery(String(option.value), query)
  );
}

// A (category, option) pairs with the other categories' current selections in at least one mapping.
export function isCombinationAvailable(
  mappings: MultiSelectMapping[],
  selectedAttributes: Record<string, string[]>,
  categoryValue: string,
  optionValue: string
): boolean {
  return mappings.some((mapping) => {
    if (mapping.attributes[categoryValue] !== optionValue) {
      return false;
    }
    return Object.entries(selectedAttributes).every(([category, selected]) => {
      if (category === categoryValue || !selected.length) {
        return true;
      }
      const mapped = mapping.attributes[category];
      return mapped != null && selected.includes(mapped);
    });
  });
}

// Untouched categories (no boxes checked) are treated as "all selected", but
// only options that still form a valid combination with other selections are filled in.
export function normalizeMappedAttributes(
  mappings: MultiSelectMapping[],
  categories: GroupItem[],
  selectedAttributes: Record<string, string[]>
): Record<string, string[]> {
  return Object.fromEntries(
    categories.map((category) => {
      const selected = selectedAttributes[category.value] ?? [];
      if (selected.length > 0) {
        return [category.value, selected];
      }
      // Unselected → expand only to values that still form a valid combination.
      const validValues = (category.options ?? [])
        .map((option) => String(option.value))
        .filter((value) =>
          isCombinationAvailable(
            mappings,
            selectedAttributes,
            category.value,
            value
          )
        );
      return [category.value, validValues];
    })
  );
}

export function resolveMappedSelections(
  mappings: MultiSelectMapping[],
  categories: GroupItem[],
  selectedAttributes: Record<string, string[]>
): string[] {
  const ids = categories.map((c) => c.value);
  if (!ids.every((id) => (selectedAttributes[id]?.length ?? 0) > 0)) return [];

  return mappings
    .filter((mapping) =>
      ids.every((id) => {
        const val = mapping.attributes[id];
        // Ignore categories omitted from the mapping.
        if (val == null) return true;
        return selectedAttributes[id]?.includes(val);
      })
    )
    .map((mapping) => mapping.value);
}

export function deriveMappedAttributesFromSelection(
  selectedValues: string[],
  mappings: MultiSelectMapping[],
  categories: GroupItem[]
): Record<string, string[]> {
  const selectedMappings = mappings.filter((mapping) =>
    selectedValues.includes(mapping.value)
  );

  return Object.fromEntries(
    categories.map((category) => {
      const values = new Set<string>();
      selectedMappings.forEach((mapping) => {
        const attributeValue = mapping.attributes[category.value];
        if (attributeValue) {
          values.add(attributeValue);
        }
      });
      return [category.value, Array.from(values)];
    })
  );
}
